import { useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import type { BrandExtractionResult } from "../../../../shared/types";
import { useExtractBrand, useCreateBrand } from "../../hooks/useBrand";

interface BrandExtractionProps {
  onComplete?: (brandId: string) => void;
}

export function BrandExtraction({ onComplete }: BrandExtractionProps) {
  const [url, setUrl] = useState("");
  const [result, setResult] = useState<BrandExtractionResult | null>(null);
  const extract = useExtractBrand();
  const createBrand = useCreateBrand();

  async function handleExtract(e: FormEvent) {
    e.preventDefault();
    const data = await extract.mutateAsync(url);
    setResult(data);
  }

  async function handleConfirm() {
    if (!result) return;
    const res = await createBrand.mutateAsync({
      name: result.brand_name,
      industry: result.industry,
      target_audience: result.target_audience,
      tone_descriptors: result.tone_descriptors,
      website_url: url,
    });
    onComplete?.(res.id);
  }

  if (result) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-slate-900">Extracted Brand Profile</h3>
        <div className="space-y-3 rounded-lg border border-slate-200 p-4">
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Brand</span>
            <p className="text-sm font-medium text-slate-900">{result.brand_name}</p>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Industry</span>
            <p className="text-sm text-slate-700">{result.industry}</p>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Target Audience</span>
            <p className="text-sm text-slate-700">{result.target_audience}</p>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Tone</span>
            <div className="mt-1 flex flex-wrap gap-2">
              {result.tone_descriptors.map((tone) => (
                <span key={tone} className="rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700">
                  {tone}
                </span>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs font-medium uppercase text-slate-400">Positioning</span>
            <p className="text-sm text-slate-700">{result.positioning_statement}</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={handleConfirm} disabled={createBrand.isPending}>
            {createBrand.isPending ? "Creating..." : "Confirm & Save"}
          </Button>
          <Button variant="secondary" onClick={() => setResult(null)}>
            Start over
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleExtract} className="space-y-4">
      <p className="text-sm text-slate-500">
        Paste your website URL and we'll extract your brand identity automatically.
      </p>
      <Input
        label="Website URL"
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://yoursite.com"
        required
      />
      {extract.isError && (
        <p className="text-sm text-red-500">
          Failed to extract brand. Please check the URL and try again.
        </p>
      )}
      <Button type="submit" disabled={extract.isPending}>
        {extract.isPending ? "Extracting..." : "Extract Brand"}
      </Button>
    </form>
  );
}
